import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    addCareTeam,
    deleteCareTeam,
    fetchCareTeams,
    fetchCurrentUserCareTeam,
    updateCareTeam,
    fetchCareTeamById,
} from '../components/Calendar/Services/CommonCareTeamServices';
import { ICareTeam } from '../Models/CareTeams/CareTeam.model';
import { SUCCESS, apiString as API_URL, ERROR } from '../utils/constants';
import { setIsLoading, setShowMessage } from './shared.slice';
import { AppThunk } from './store';
import ITeamOptimizerResult from '../Models/Teams/TeamOptimizerResult.model';
import axios from 'axios';
import { Member } from '../Models/Member/Member.model';

export interface ICareTeamsSlice {
    isAddCareTeamModalOpen: boolean;
    includeUnassignedMembers: boolean;
    isConfirmDeleteTeamModalOpen: boolean;
    careTeams: ICareTeam[];
    selectedTeam: ICareTeam | null;
    selectedOptimizerTeamIds: string[];
    expandedRoleIndex: number | null;
    optimizedTeamResults: ITeamOptimizerResult[];
    selectedOptimizerMembers: Member[];
}

const initialState: ICareTeamsSlice = {
    isAddCareTeamModalOpen: false,
    includeUnassignedMembers: true,
    isConfirmDeleteTeamModalOpen: false,
    careTeams: [],
    selectedTeam: null,
    selectedOptimizerTeamIds: [],
    expandedRoleIndex: null,
    optimizedTeamResults: [],
    selectedOptimizerMembers: [],
};

// Alternates with no provider would override the team's provider with "Unassigned", so drop them.
const withAssignedAlternates = (team: ICareTeam | null): ICareTeam | null =>
    team ? { ...team, TeamRoleAlternates: (team.TeamRoleAlternates || []).filter((alt) => !!alt?.AssignedProviderId) } : team;

const careTeamsSlice = createSlice({
    name: 'careTeams',
    initialState,
    reducers: {
        _isAddCareTeamModalOpen(state, action: PayloadAction<boolean>) {
            state.isAddCareTeamModalOpen = action.payload;
        },
        _isConfirmDeleteTeamModalOpen(state, action: PayloadAction<boolean>) {
            state.isConfirmDeleteTeamModalOpen = action.payload;
        },
        _setCareTeams(state, action: PayloadAction<ICareTeam[]>) {
            state.careTeams = action.payload?.map(withAssignedAlternates);
        },
        _setSelectedTeam(state, action: PayloadAction<ICareTeam | null>) {
            console.log('Redux _setSelectedTeam called with:', action.payload?.TeamRoleAlternates);
            state.selectedTeam = withAssignedAlternates(action.payload);
        },
        _setExpandedRoleIndex(state, action: PayloadAction<number | null>) {
            state.expandedRoleIndex = action.payload;
        },
        _setOptimizedTeamResults(state, action: PayloadAction<ITeamOptimizerResult[]>) {
            state.optimizedTeamResults = action.payload;
        },
        _setSelectedOptimizerTeamIds(state, action: PayloadAction<string[]>) {
            state.selectedOptimizerTeamIds = action.payload;
        },
        _setSelectedOptimizerMembers(state, action: PayloadAction<Member[]>) {
            state.selectedOptimizerMembers = action.payload;
        },
        _setIncludeUnassignedMembers(state, action: PayloadAction<boolean>) {
            state.includeUnassignedMembers = action.payload;
        },
    },
});

const {
    _setCareTeams,
    _setSelectedTeam,
    _isAddCareTeamModalOpen,
    _isConfirmDeleteTeamModalOpen,
    _setExpandedRoleIndex,
    _setOptimizedTeamResults,
    _setIncludeUnassignedMembers,
    _setSelectedOptimizerMembers,
    _setSelectedOptimizerTeamIds,
} = careTeamsSlice.actions;

export const setIncludeUnassignedMembers =
    (includeUnassignedMembers: boolean): AppThunk =>
        async (dispatch) => {
            dispatch(_setIncludeUnassignedMembers(includeUnassignedMembers));
        };

export const setSelectedOptimizerMembers =
    (members: Member[]): AppThunk =>
        async (dispatch) => {
            dispatch(_setSelectedOptimizerMembers(members));
        };

export const setSelectedOptimizerTeamIds =
    (teamIds: string[]): AppThunk =>
        async (dispatch) => {
            dispatch(_setSelectedOptimizerTeamIds(teamIds));
        };

export const setCareTeams =
    (careTeams: ICareTeam[]): AppThunk =>
        async (dispatch) => {
            dispatch(_setCareTeams(careTeams));
        };

export const setSelectedTeam =
    (team: ICareTeam): AppThunk =>
        async (dispatch) => {
            dispatch(_setSelectedTeam(team));
        };

export const setExpandedRoleIndex =
    (index: number | null): AppThunk =>
        async (dispatch) => {
            dispatch(_setExpandedRoleIndex(index));
        };

export const setOptimizedTeamResults =
    (results: ITeamOptimizerResult[]): AppThunk =>
        async (dispatch) => {
            dispatch(_setOptimizedTeamResults(results));
        };

export const fetchOptimizedTeamResults = (): AppThunk => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const memberIds = getState().careTeams.selectedOptimizerMembers.map((member) => member.Id);
    try {
        const request = {
            SelectedTeamIds: getState().careTeams.selectedOptimizerTeamIds,
            IncludeUnassignedMembers: getState().careTeams.includeUnassignedMembers,
            MemberIds: memberIds,
        };
        const response = await axios.post(`${API_URL}/TeamOptimizer/optimize`, request);
        dispatch(_setOptimizedTeamResults(response.data));
        dispatch(setIsLoading(false));
    } catch (error) {
        dispatch(setShowMessage(true, error.response.data, ERROR));
        dispatch(setIsLoading(false));
    }
};

export const assignToTeam =
    (teamId: string, members: string[], postSaveHandle: Function = null): AppThunk =>
        async (dispatch) => {
            try {
                await axios.put(`${API_URL}/careteams/assignmembers?careTeamId=${teamId}`, members);
                if (postSaveHandle) {
                    postSaveHandle();
                }
            } catch (error) {
                console.log(error);
                dispatch(setShowMessage(true, 'Error assigning to team.', ERROR));
            }
        };

export const clearResults = (): AppThunk => async (dispatch) => {
    dispatch(_setOptimizedTeamResults([]));
};

export const openConfirmDeleteTeamModal = (): AppThunk => async (dispatch) => {
    dispatch(_isConfirmDeleteTeamModalOpen(true));
};

export const closeConfirmDeleteTeamModal = (): AppThunk => async (dispatch) => {
    dispatch(_isConfirmDeleteTeamModalOpen(false));
};

export const openAddCareTeamModal = (): AppThunk => async (dispatch) => {
    dispatch(_isAddCareTeamModalOpen(true));
};

export const closeAddCareTeamModal = (): AppThunk => async (dispatch) => {
    dispatch(_isAddCareTeamModalOpen(false));
};

export const addTeam =
    (careTeam: ICareTeam): AppThunk =>
        async (dispatch, getState) => {
            dispatch(setIsLoading(true));
            try {
                const response = await addCareTeam(careTeam);
                if (response.status === 201) {
                    dispatch(setCareTeams([...getState().careTeams.careTeams, response.data]));
                }
            } finally {
                dispatch(setIsLoading(false));
            }
        };

export const updateTeam =
    (careTeam: ICareTeam): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsLoading(true));
        try {
            const response = await updateCareTeam(careTeam);
            if (response.status === 200) {
                const updatedCareTeams = getState().careTeams.careTeams.map((team) => (team.Id === careTeam.Id ? response.data : team));
                dispatch(setCareTeams(updatedCareTeams));
                // Also update selectedTeam if it's the team being updated
                const currentSelectedTeam = getState().careTeams.selectedTeam;
                if (currentSelectedTeam?.Id === careTeam.Id) {
                    dispatch(_setSelectedTeam(response.data));
                }
                dispatch(setShowMessage(true, `${careTeam.Name} has been updated.`, SUCCESS));
            }
        } catch (error) {
            console.error('Error updating team:', error);
            dispatch(setShowMessage(true, error.response?.data ?? 'An error updating the team has occurred', 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchTeams =
    (includeSoftDeleted?: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const response = await fetchCareTeams(includeSoftDeleted);
            if (response.status === 200) {
                dispatch(setCareTeams(response.data));
            }
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchTeamDetail =
    (teamId: string): AppThunk =>
    async (dispatch) => {
        try {
            console.log('fetchTeamDetail called for teamId:', teamId);
            const response = await fetchCareTeamById(teamId);
            console.log('fetchTeamDetail response:', response.data?.TeamRoleAlternates);
            if (response.status === 200) {
                dispatch(_setSelectedTeam(response.data));
            }
            return response.data;
        } catch (error) {
            console.error('Error fetching team detail:', error);
            dispatch(setShowMessage(true, 'Error loading team details', 'error'));
            throw error;
        }
    };

export const deleteSelectedTeam = (): AppThunk => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    try {
        const selectedTeam = getState().careTeams.selectedTeam;
        if (selectedTeam) {
            const response = await deleteCareTeam(selectedTeam.Id);
            if (response.status === 204) {
                dispatch(setCareTeams(getState().careTeams.careTeams.filter((team) => team.Id !== selectedTeam.Id)));
                dispatch(setSelectedTeam(null));
                dispatch(setShowMessage(true, 'Team deleted successfully', 'success'));
            } else if (response.status === 404) {
                dispatch(setShowMessage(true, 'Invalid team specified. Cannot delete.', 'error'));
            }
        }
    } catch (error) {
        console.error('Error deleting team:', error);
        dispatch(setShowMessage(true, 'Error deleting team', 'error'));

        // re-fetch teams to ensure the UI is in sync with the server
        const response = await fetchCareTeams(false);
        if (response.status === 200) {
            dispatch(setCareTeams(response.data));
        }

        throw error;
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const selectCurrentUsersCareTeam = (): AppThunk => async (dispatch) => {
    try {
        const response = await fetchCurrentUserCareTeam();
        if (response.status === 200) {
            const teams = response.data;
            if (teams.length > 0) {
                dispatch(setSelectedTeam(teams[0]));
                dispatch(setCareTeams(teams));
            }
        }
    } catch (error) {
        console.error('Error fetching provider team:', error);
        dispatch(setShowMessage(true, 'Error fetching provider teams', 'error'));
    }
};

export const fetchCareTeamByMemberId =
    (memberId: string): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const response = await axios.get(`${API_URL}/CareTeams/bymemberid?memberId=${memberId}`);
                dispatch(_setCareTeams(response.data ? [response.data] : []));
                dispatch(_setSelectedTeam(response.data || null));
            } catch (error) {
                dispatch(_setCareTeams([]));
                dispatch(_setSelectedTeam(null));
            } finally {
                dispatch(setIsLoading(false));
            }
        };

export const setCareTeam =
    (memberId: string, careTeamId: string): AppThunk =>
        async (dispatch) => {
            dispatch(setIsLoading(true));
            try {
                const response = await axios.put(`${API_URL}/CareTeams/assignmember?memberId=${memberId}&careTeamId=${careTeamId}`);
                dispatch(_setCareTeams(response.data ? [response.data] : []));
                dispatch(_setSelectedTeam(response.data || null));
            } finally {
                dispatch(setIsLoading(false));
            }
        };

export default careTeamsSlice.reducer;


